import IconChevronDown from 'assets/icons/chevron-down.svg';
import cn from 'classnames';
import Button from 'components/button-new';
import LazyMotionWrapper from 'components/lazy-motion-wrapper';
import { useRouteContext } from 'contexts/route';
import { AnimatePresence, m } from 'motion/react';
import Image from 'next/legacy/image';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import BreedSegmentationSlider from '../breed-segmentation-slider';
import ModalInformation from '../modal-information';
import PetNeedsSegmentationDropDown from '../segementation-drop-down';
import bowlWithUspImageCat from './230803_pd_onsite_petneed_bowl_cat-home.png';
import bowlWithUspImageDog from './230803_pd_onsite_petneed_bowl_dog_fte.png';

interface Props {
  isMobile: boolean;
  petType: 'dog' | 'cat';
}

const NeedBreedSelector: React.FC<Props> = ({ isMobile, petType }) => {
  const { previousPath } = useRouteContext();
  const [isDropdownExpanded, setIsDropdownExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Expand dropdown if user comes from Home/Home
  useEffect(() => {
    if (previousPath === '/' && petType === 'dog') setIsDropdownExpanded(true);

    return () => {
      setIsDropdownExpanded(false);
    };
  }, [petType, previousPath]);

  const getHeaderHeight = (): number => {
    try {
      const html = document.querySelector('html');
      if (!html) throw new Error();
      const px = getComputedStyle(html).getPropertyValue('--header-height');
      const number = parseInt(px.replace('px', ''));
      if (isNaN(number)) {
        throw new Error();
      }

      return number;
    } catch (error) {
      return 70;
    }
  };

  return (
    <>
      <ModalInformation isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
      <div>
        <div
          className="text-text-dark-bg-secondary mb-6 text-center lg:mb-8 lg:text-xl"
          ref={ref}
        >
          <FormattedMessage id="landingpage:home:sub-headline" />
        </div>
        <LazyMotionWrapper>
          <div
            className={cn(
              'container lg:mb-16 lg:flex lg:flex-col lg:items-center',
              isDropdownExpanded && 'mb-8'
            )}
          >
            <Button
              data-test="landingpage:home:need-selector-cta"
              className="mb-6 flex items-center gap-2 lg:w-96"
              size="large"
              onClick={() => {
                setIsDropdownExpanded(!isDropdownExpanded);
                if (!isDropdownExpanded) {
                  setTimeout(() => {
                    if (ref.current && !!ref.current.offsetTop) {
                      window.scrollTo({
                        top: ref.current.offsetTop + getHeaderHeight(),
                        behavior: 'smooth',
                      });
                    }
                  }, 500);
                }
              }}
            >
              <FormattedMessage id="landingpage:home:cta-button" />
              <m.span
                animate={{ rotate: isDropdownExpanded ? -180 : 0 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <IconChevronDown className="h-2 w-2" />
              </m.span>
            </Button>

            {isDropdownExpanded && (
              <AnimatePresence>
                <m.div
                  className="w-full lg:mt-2.5 lg:max-w-3xl"
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={{
                    open: { opacity: 1, height: 'auto' },
                    collapsed: { opacity: 0, height: 0 },
                  }}
                  transition={{
                    duration: 0.8,
                    ease: [0.04, 0.62, 0.23, 0.98],
                  }}
                >
                  {petType === 'dog' && (
                    <>
                      <div className="text-other-white mb-4 inline-block lg:hidden">
                        <FormattedMessage
                          id="landingpage:need-capture:which-need-most-applied"
                          values={{
                            b: (chunk) => <b>{chunk}</b>,
                          }}
                        />
                      </div>
                      <div className="text-other-white mb-4 hidden w-full text-center lg:inline-block">
                        <FormattedMessage
                          id="landingpage:need-capture:which-need-most-applied-dog"
                          values={{
                            b: (chunk) => <b>{chunk}</b>,
                            br: <br />,
                            a: (chunk) => (
                              <a
                                className="text-other-white inline cursor-pointer underline"
                                onClick={() => {
                                  setIsModalOpen(true);
                                }}
                              >
                                {chunk}
                              </a>
                            ),
                          }}
                        />
                      </div>
                    </>
                  )}
                  {petType === 'cat' && (
                    <>
                      <div className="text-other-white mb-4 w-full lg:text-center">
                        <FormattedMessage
                          id="landingpage:need-capture:which-need-most-applied-cat"
                          values={{
                            b: (chunk) => <b>{chunk}</b>,
                          }}
                        />
                      </div>
                    </>
                  )}
                  <div className="lg:flex lg:justify-center lg:gap-5">
                    <div className="basis-1/2">
                      <PetNeedsSegmentationDropDown
                        isOpen={isDropdownExpanded}
                        location="home-fte"
                        onSelectCallback={() => setIsDropdownExpanded(false)}
                        petType={petType}
                      />
                    </div>
                    {!isMobile && petType === 'dog' && (
                      <div className="basis-1/2">
                        <BreedSegmentationSlider
                          location={'home-fte'}
                          showAsSelectButton={!isMobile}
                        />
                      </div>
                    )}
                  </div>
                  {isMobile && petType === 'dog' && (
                    <>
                      <div className="text-text-dark-bg-contrast-white my-4 font-medium">
                        <FormattedMessage id="landingpage:home:breed-selector:headline" />
                      </div>
                      <BreedSegmentationSlider location={'home-fte'} />
                    </>
                  )}
                </m.div>
              </AnimatePresence>
            )}
          </div>
        </LazyMotionWrapper>
        <div
          className={cn('grid lg:hidden', !isDropdownExpanded && 'mb-8')}
          style={{
            gridTemplateRows: isDropdownExpanded ? '0fr' : '1fr',
            transition: '0.8s grid-template-rows ease',
          }}
        >
          <Image
            src={petType === 'dog' ? bowlWithUspImageDog : bowlWithUspImageCat}
            layout={'responsive'}
            objectFit={'cover'}
            quality={100}
            priority
          />
        </div>
      </div>
    </>
  );
};

export default NeedBreedSelector;
